import { Link } from "react-router-dom";
import {
  Users,
  Globe,
  Lock,
  Hash,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";

/* ----------------------------
   TEMP draft helpers (no DB)
   Club landing is ClubHome at `/clubs/:slug`.
----------------------------- */
function loadClubDraft(slug) {
  if (!slug) return null;

  try {
    const raw = localStorage.getItem(`clubDraft:${slug}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function pickFirstNonNull(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return null;
}

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function getClubSlug(club) {
  const direct = pickFirstNonNull(
    club?.slug,
    club?.club_slug,
    club?.clubSlug,
    club?.handle,
    club?.path_slug,
    club?.pathSlug
  );

  if (direct) return String(direct).trim();

  const fromName = slugify(club?.name);
  return fromName || null;
}

/**
 * Resolve image URLs safely:
 * - strips accidental "undefined/" prefix
 * - keeps absolute URLs
 * - converts R2 keys like "clubs/<slug>/avatar_..." using REACT_APP_R2_PUBLIC_BASE
 * - converts non-rooted paths into rooted "/..."
 * - optionally prefixes REACT_APP_API_BASE for backend-served paths
 */
function resolveImageUrl(url) {
  if (!url) return null;

  let s = String(url).trim();
  if (!s) return null;

  if (s.startsWith("undefined/")) s = s.replace(/^undefined\/+/, "");

  if (/^https?:\/\//i.test(s)) return s;

  const r2Base = process.env.REACT_APP_R2_PUBLIC_BASE || "";
  if (r2Base && /^clubs\//i.test(s)) {
    return `${r2Base.replace(/\/+$/, "")}/${s.replace(/^\/+/, "")}`;
  }

  const rooted = s.startsWith("/") ? s : `/${s}`;
  const apiBase = process.env.REACT_APP_API_BASE || "";

  if (!apiBase) return rooted;
  return `${apiBase.replace(/\/+$/, "")}${rooted}`;
}

export default function ClubCard({ club }) {
  const memberCount = Number(
    club?.member_count ?? club?.members_count ?? club?.members ?? 0
  );

  const visibility = String(club?.visibility || "public").toLowerCase();
  const VisibilityIcon = visibility === "private" ? Lock : Globe;

  const clubSlug = getClubSlug(club);

  const clubDraft = club?.__draft || loadClubDraft(clubSlug);

  const avatarRaw =
    clubDraft?.avatar_image_url ||
    club?.avatar_image_url ||
    club?.avatarImageUrl ||
    club?.icon_url ||
    null;

  const avatar = resolveImageUrl(avatarRaw);

  const href = !clubSlug ? "#" : `/clubs/${encodeURIComponent(clubSlug)}`;

  return (
    <Link
      to={href}
      onClick={(e) => {
        if (!clubSlug) {
          e.preventDefault();
          console.warn("[ClubCard] Missing slug for club:", club);
        }
      }}
      className="
        group relative block overflow-hidden
        rounded-3xl
        border border-white/10
        bg-[#0B0F16]/66
        backdrop-blur-md
        transition
        hover:border-white/18
        hover:bg-[#0B0F16]/78
        hover:-translate-y-[2px]
        focus:outline-none
        focus-visible:ring-2 focus-visible:ring-cyan-300/25
      "
    >
      <div className="absolute left-0 top-0 h-1 w-full bg-cyan-300/40" />

      <div
        className="
          pointer-events-none absolute inset-0
          opacity-[0.08]
          bg-[linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)]
          [background-size:26px_26px]
        "
      />

      <div
        className="
          pointer-events-none absolute inset-0
          opacity-0 group-hover:opacity-100
          transition duration-300
          bg-[radial-gradient(circle_at_18%_22%,rgba(34,211,238,0.12)_0,transparent_44%),radial-gradient(circle_at_85%_78%,rgba(217,70,239,0.09)_0,transparent_46%)]
        "
      />

      <div className="relative p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex flex-wrap items-center gap-1.5 sm:gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/35 px-2 py-1 text-[8px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.22em] uppercase text-white/70">
              <Hash className="h-3 w-3 text-cyan-200/75" />
              Club
            </span>

            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/35 px-2 py-1 text-[9px] sm:text-[11px] text-white/70 capitalize">
              <VisibilityIcon className="h-3 w-3 text-cyan-200/60" />
              {club?.visibility || "Public"}
            </span>

            {club?.is_official ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-300/25 bg-fuchsia-300/10 px-2 py-1 text-[9px] sm:text-[11px] text-fuchsia-200">
                <ShieldCheck className="h-3 w-3 text-fuchsia-200/85" />
                Official
              </span>
            ) : null}
          </div>

          <div className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/35 px-2 py-1 text-[9px] sm:text-[11px] text-white/75">
            <Users className="h-3 w-3 text-white/55" />
            {memberCount.toLocaleString()}
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2.5 sm:gap-3.5">
          <div className="relative h-10 w-10 sm:h-12 sm:w-12 shrink-0 rounded-2xl overflow-hidden border border-white/10 bg-white/[0.05]">
            {avatar ? (
              <img
                src={avatar}
                alt={`${club?.name || "Club"} avatar`}
                className="absolute inset-0 h-full w-full object-cover object-center"
                loading="lazy"
                draggable={false}
                onError={() => {
                  console.log("[ClubCard] avatar failed to load:", avatar, {
                    avatarRaw,
                    slug: clubSlug,
                  });
                }}
              />
            ) : (
              <div className="absolute inset-0 bg-white/[0.03]" />
            )}

            <div className="pointer-events-none absolute inset-0 ring-1 ring-cyan-300/18" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-white font-semibold tracking-tight truncate text-[13.5px] sm:text-[16px] leading-tight">
              {club?.name || "Untitled Club"}
            </div>

            {club?.description ? (
              <div className="mt-1.5 text-[12px] sm:text-sm text-white/70 leading-snug line-clamp-2">
                {club.description}
              </div>
            ) : (
              <div className="mt-1.5 text-[12px] sm:text-sm text-white/45 leading-snug">
                A place to chat, post, and build a community.
              </div>
            )}

            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/28 px-2 py-0.5 text-[9px] sm:text-[11px] text-white/70">
                <span className="text-cyan-200/70">#</span> general
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/28 px-2 py-0.5 text-[9px] sm:text-[11px] text-white/70">
                <span className="text-fuchsia-200/70">#</span> art
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/28 px-2 py-0.5 text-[9px] sm:text-[11px] text-white/70">
                <span className="text-cyan-200/70">#</span> clips
              </span>
            </div>
          </div>
        </div>

        <div className="mt-3.5 sm:mt-5">
          <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/20 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={[
                  "h-2 w-2 rounded-full shrink-0",
                  visibility === "private" ? "bg-white/25" : "bg-cyan-300/70",
                ].join(" ")}
              />
              <span className="text-[9.5px] sm:text-[11px] text-white/55 truncate">
                {visibility === "private" ? "Invite only" : "Open to everyone"}
              </span>
            </div>

            <ChevronRight
              className="
                h-4 w-4 text-white/40
                transition
                group-hover:translate-x-0.5
                group-hover:text-white/70
              "
            />
          </div>
        </div>
      </div>
    </Link>
  );
}